import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SEO_DEFAULTS, seoUrl } from '../constants/seo';

export interface SEOMetaProps {
  /** Page title (e.g. "Pomodoro Timer Online | Focuzon"). Omit to use default. */
  title?: string;
  /** Meta description. Omit to use default. */
  description?: string;
  /** Canonical path (e.g. "/pomodoro-timer"). Omit to use current pathname. */
  canonicalPath?: string;
  /** Open Graph title. Defaults to title or SEO_DEFAULTS.ogTitle */
  ogTitle?: string;
  /** Open Graph description. Defaults to description or SEO_DEFAULTS.ogDescription */
  ogDescription?: string;
  /** Open Graph image URL. Optional. */
  ogImage?: string;
  /** Open Graph type. Defaults to "website" */
  ogType?: string;
  /** If true, do not render canonical (e.g. for duplicate content). */
  noCanonical?: boolean;
}

/**
 * Reusable SEO meta component. Renders title, meta description, canonical,
 * Open Graph and Twitter tags. Use on every page; pass props to override defaults.
 * When used with no props, applies global defaults and canonical from current URL.
 */
const SEOMeta: React.FC<SEOMetaProps> = ({
  title = SEO_DEFAULTS.title,
  description = SEO_DEFAULTS.description,
  canonicalPath,
  ogTitle = title,
  ogDescription = description,
  ogImage,
  ogType = SEO_DEFAULTS.ogType,
  noCanonical = false,
}) => {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const canonical = seoUrl(path);
  const ogUrl = seoUrl(path);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {!noCanonical && <link rel="canonical" href={canonical} />}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content={ogType} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content={SEO_DEFAULTS.twitterCard} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
    </Helmet>
  );
};

export default SEOMeta;
